import {
  type ComponentItemConfig,
  type RowOrColumnItemConfig,
  type StackItemConfig,
  isComponentItemConfig,
  resolveItemConfigWithComponentReorderEnabledDefault,
} from '../config/config';
import {
  type ResolvedRowOrColumnItemConfig,
  type ResolvedStackItemConfig,
} from '../config/resolved-config';
import { Splitter } from '../controls/splitter';
import { AssertError, UnexpectedNullError } from '../errors/internal-error';
import { LayoutManager } from '../layout-manager';
import { DomConstants } from '../utils/dom-constants';
import {
  ComponentType,
  ItemType,
  SerializableValue,
  SizeUnit,
  WidthOrHeightPropertyName,
} from '../utils/types';
import {
  getElementHeight,
  getElementWidth,
  getElementWidthAndHeight,
  numberToPixels,
  pixelsToNumber,
  setElementHeight,
  setElementWidth,
} from '../utils/utils';
import { ComponentItem } from './component-item';
import { ContentItem } from './content-item';

/** @internal */
export interface RowOrColumnAbsoluteSizes {
  itemSizes: number[];
  additionalPixel: number;
  totalSize: number;
  crossAxisSize: number;
}

/** @internal */
export function getRowOrColumnElementDimensionSize(
  element: HTMLElement,
  dimension: WidthOrHeightPropertyName,
): number {
  if (dimension === 'width') {
    return getElementWidth(element);
  } else {
    return getElementHeight(element);
  }
}

/** @internal */
export function setRowOrColumnElementDimensionSize(
  element: HTMLElement,
  dimension: WidthOrHeightPropertyName,
  value: number,
): void {
  if (dimension === 'width') {
    return setElementWidth(element, value);
  } else {
    return setElementHeight(element, value);
  }
}

/** @internal */
export function createRowOrColumnElement(
  document: Document,
  isColumn: boolean,
): HTMLDivElement {
  const element = document.createElement('div');
  element.classList.add(DomConstants.ClassName.Item);
  if (isColumn) {
    element.classList.add(DomConstants.ClassName.Column);
  } else {
    element.classList.add(DomConstants.ClassName.Row);
  }
  return element;
}

/**
 * Provides row or column behavior.
 * @public
 */
export class RowOrColumn extends ContentItem {
  /** @internal */
  private readonly _childElementContainer: HTMLElement;
  /** @internal */
  private readonly _configType: 'row' | 'column';
  /** @internal */
  private readonly _isColumn: boolean;
  /** @internal */
  private readonly _splitterSize: number;
  /** @internal */
  private readonly _splitterGrabSize: number;
  /** @internal */
  private readonly _dimension: WidthOrHeightPropertyName;
  /** @internal */
  private readonly _splitter: Splitter[] = [];
  /** @internal */
  private _splitterPosition: number | null;
  /** @internal */
  private _splitterMinPosition: number | null;
  /** @internal */
  private _splitterMaxPosition: number | null;
  /** Deferred resize scheduled after a splitter drag completes. */
  private _resizeFrame: number | undefined;

  /** Prevents unsupported direct construction. @public */
  constructor(_nonConstructible: never, ..._args: never[]);
  /** @internal */
  constructor(
    isColumn: boolean,
    layoutManager: LayoutManager,
    config: ResolvedRowOrColumnItemConfig,
    rowOrColumnParent: ContentItem,
  );
  /** @internal */
  constructor(
    isColumn: boolean,
    layoutManager: LayoutManager,
    config: ResolvedRowOrColumnItemConfig,
    /** @internal */
    private _rowOrColumnParent: ContentItem,
  ) {
    super(
      layoutManager,
      config,
      _rowOrColumnParent,
      createRowOrColumnElement(document, isColumn),
    );

    this.isRow = !isColumn;
    this.isColumn = isColumn;

    this._childElementContainer = this.element;
    this._splitterSize = layoutManager.layoutConfig.dimensions.borderWidth;
    this._splitterGrabSize =
      layoutManager.layoutConfig.dimensions.borderGrabWidth;
    this._isColumn = isColumn;
    this._dimension = isColumn ? 'height' : 'width';
    this._splitterPosition = null;
    this._splitterMinPosition = null;
    this._splitterMaxPosition = null;

    switch (config.type) {
      case ItemType.row:
      case ItemType.column:
        this._configType = config.type;
        break;
      default:
        throw new AssertError('ROCCCT00925');
    }
  }

  /** @internal */
  override destroy(): void {
    if (this._isDestroyed && !this._destroyCleanupFailed) {
      return;
    }

    let firstError: unknown;
    let hasError = false;
    const attempt = (action: () => void) => {
      try {
        action();
      } catch (error) {
        if (!hasError) firstError = error;
        hasError = true;
      }
    };
    if (this._resizeFrame !== undefined) {
      globalThis.cancelAnimationFrame(this._resizeFrame);
      this._resizeFrame = undefined;
    }
    const remainingSplitters: Splitter[] = [];
    for (const splitter of this._splitter) {
      let destroyed = false;
      attempt(() => {
        splitter.destroy();
        destroyed = true;
      });
      if (!destroyed) {
        remainingSplitters.push(splitter);
      }
    }
    this._splitter.length = 0;
    this._splitter.push(...remainingSplitters);
    if (this._resizeFrame !== undefined) {
      globalThis.cancelAnimationFrame(this._resizeFrame);
      this._resizeFrame = undefined;
    }
    attempt(() => super.destroy());
    if (hasError) {
      this._destroyCleanupFailed = true;
      throw firstError;
    }
    this._destroyCleanupFailed = false;
  }

  /** Creates component. */
  newComponent(
    componentType: ComponentType,
    componentState?: SerializableValue,
    title?: string,
    index?: number,
  ): ComponentItem {
    const itemConfig: ComponentItemConfig = {
      type: 'component',
      componentType,
      componentState,
      title,
    };
    return this.newItem(itemConfig, index) as ComponentItem;
  }

  /** Adds component. */
  addComponent(
    componentType: ComponentType,
    componentState?: SerializableValue,
    title?: string,
    index?: number,
  ): number {
    const itemConfig: ComponentItemConfig = {
      type: 'component',
      componentType,
      componentState,
      title,
    };
    return this.addItem(itemConfig, index);
  }

  /** Creates item. */
  newItem(
    itemConfig: RowOrColumnItemConfig | StackItemConfig | ComponentItemConfig,
    index?: number,
  ): ContentItem {
    index = this.addItem(itemConfig, index);
    const createdItem = this.contentItems[index];

    if (ContentItem.isStack(createdItem) && isComponentItemConfig(itemConfig)) {
      // createdItem is a Stack which was created to hold wanted component.  Return component
      return createdItem.contentItems[0];
    } else {
      return createdItem;
    }
  }

  /** Adds item. */
  addItem(
    itemConfig: RowOrColumnItemConfig | StackItemConfig | ComponentItemConfig,
    index?: number,
  ): number {
    this.validateInsertionIndex(index);
    this.layoutManager.checkMinimiseMaximisedStack();
    const resolvedItemConfig =
      resolveItemConfigWithComponentReorderEnabledDefault(
        itemConfig,
        this.layoutManager.layoutConfig.settings.reorderEnabled,
      );
    const contentItem = this.layoutManager.createAndInitContentItem(
      resolvedItemConfig,
      this,
    );
    return this.addChild(contentItem, index, false);
  }

  /**
   * Add a new contentItem to the Row or Column
   *
   * @param contentItem -
   * @param index - The position of the new item within the Row or Column.
   *                If no index is provided the item will be added to the end
   * @param suspendResize - If true the items won't be resized. This will leave the item in
   *                        an inconsistent state and is only intended to be used if multiple
   *                        children need to be added in one go and resize is called afterwards
   *
   * @returns
   */
  override addChild(
    contentItem: ContentItem,
    index?: number,
    suspendResize?: boolean,
  ): number {
    // contentItem = this.layoutManager._$normalizeContentItem(contentItem, this);

    this.validateInsertionIndex(index);
    index ??= this.contentItems.length;

    if (this.contentItems.length > 0) {
      const splitterElement = this.createSplitter(
        Math.max(0, index - 1),
      ).element;

      if (index > 0) {
        this.contentItems[index - 1].element.insertAdjacentElement(
          'afterend',
          splitterElement,
        );
        splitterElement.insertAdjacentElement('afterend', contentItem.element);
      } else {
        this.contentItems[0].element.insertAdjacentElement(
          'beforebegin',
          splitterElement,
        );
        splitterElement.insertAdjacentElement(
          'beforebegin',
          contentItem.element,
        );
      }
    } else {
      this._childElementContainer.appendChild(contentItem.element);
    }

    super.addChild(contentItem, index);

    const newItemSize = (1 / this.contentItems.length) * 100;

    if (suspendResize) {
      this.emitBaseBubblingEvent('stateChanged');
      return index;
    }

    for (const indexedContentItem of this.contentItems) {
      if (indexedContentItem === contentItem) {
        contentItem.size = newItemSize;
      } else {
        const itemSize = (indexedContentItem.size *= (100 - newItemSize) / 100);
        indexedContentItem.size = itemSize;
      }
    }

    this.updateSize(false);
    this.emitBaseBubblingEvent('stateChanged');

    return index;
  }

  private validateInsertionIndex(index: number | undefined): void {
    if (
      index !== undefined &&
      (!Number.isInteger(index) ||
        index < 0 ||
        index > this.contentItems.length)
    ) {
      throw new RangeError(
        `Insertion index must be an integer between 0 and ${this.contentItems.length}`,
      );
    }
  }

  /**
   * Removes a child of this element
   *
   * @param contentItem -
   * @param keepChild - If true the child will be removed, but not destroyed
   *
   */
  override removeChild(contentItem: ContentItem, keepChild: boolean): void {
    const index = this.contentItems.indexOf(contentItem);
    const splitterIndex = Math.max(index - 1, 0);

    if (index === -1) {
      throw new Error(
        "Can't remove child. ContentItem is not child of this Row or Column",
      );
    }

    if (!keepChild) {
      contentItem.destroy();
    }

    /**
     * Remove the splitter before the item or after if the item happens
     * to be the first in the row/column
     */
    if (this._splitter[splitterIndex]) {
      this._splitter[splitterIndex].destroy();
      this._splitter.splice(splitterIndex, 1);
    }

    super.removeChild(contentItem, true);

    if (
      !keepChild &&
      this.contentItems.length === 1 &&
      this.isClosable &&
      this.popInParentIds.length === 0
    ) {
      this.collapseToSingleChild();
    } else {
      this.updateSize(false);
      this.emitBaseBubblingEvent('stateChanged');
    }
  }

  /** @internal */
  checkCollapse(): void {
    if (this.isClosable && this.popInParentIds.length === 0) {
      if (this.contentItems.length === 0) {
        this.remove();
      } else if (this.contentItems.length === 1) {
        this.collapseToSingleChild();
      }
    }
  }

  /** @internal */
  private collapseToSingleChild(): void {
    const childItem = this.contentItems[0];
    this.contentItems.length = 0;
    try {
      this._rowOrColumnParent.replaceChild(this, childItem, true);
    } catch (error) {
      this.contentItems.push(childItem);
      throw error;
    }
  }

  /**
   * Replaces a child of this Row or Column with another contentItem
   */
  override replaceChild(
    oldChild: ContentItem,
    newChild: ContentItem,
    destroyOldChild = false,
  ): void {
    const size = oldChild.size;
    super.replaceChild(oldChild, newChild, destroyOldChild);
    newChild.size = size;
    this.updateSize(false);
    this.emitBaseBubblingEvent('stateChanged');
  }

  /**
   * Called whenever the dimensions of this item or one of its parents change
   */
  override updateSize(force: boolean): void {
    this.layoutManager.beginVirtualSizedContainerAdding();
    try {
      this.updateNodeSize();
      this.updateContentItemsSize(force);
    } finally {
      this.layoutManager.endVirtualSizedContainerAdding();
    }
  }

  /**
   * Invoked recursively by the layout manager. ContentItem.init appends
   * the contentItem's DOM elements to the container, RowOrColumn init adds splitters
   * in between them
   * @internal
   */
  override init(): void {
    if (this.isInitialised) return;

    this.updateNodeSize();

    for (const contentItem of this.contentItems) {
      this._childElementContainer.appendChild(contentItem.element);
    }

    super.init();

    for (let i = 0; i < this.contentItems.length - 1; i++) {
      this.contentItems[i].element.insertAdjacentElement(
        'afterend',
        this.createSplitter(i).element,
      );
    }

    this.initContentItems();
  }

  /** Performs the to config operation. */
  toConfig(): ResolvedRowOrColumnItemConfig {
    const result: ResolvedRowOrColumnItemConfig = {
      type: this.type as 'row' | 'column',
      content: this.calculateConfigContent() as (
        ResolvedRowOrColumnItemConfig | ResolvedStackItemConfig
      )[],
      size: this.size,
      sizeUnit: this.sizeUnit,
      minSize: this.minSize,
      minSizeUnit: this.minSizeUnit,
      id: this.id,
      isClosable: this.isClosable,
      popInParentIds: [...this.popInParentIds],
    };
    return result;
  }

  /** @internal */
  protected override setParent(parent: ContentItem): void {
    this._rowOrColumnParent = parent;
    super.setParent(parent);
  }

  /** @internal */
  private updateNodeSize(): void {
    if (this.contentItems.length > 0) {
      this.calculateRelativeSizes();
      this.setAbsoluteSizes();
    }
    this.emitBaseBubblingEvent('stateChanged');
    this.emit('resize');
  }

  /**
   * Turns the relative sizes calculated by calculateRelativeSizes into
   * absolute pixel values and applies them to the children's DOM elements
   *
   * Assigns additional pixels to counteract Math.floor
   * @internal
   */
  private setAbsoluteSizes() {
    const absoluteSizes = this.calculateAbsoluteSizes();

    for (let i = 0; i < this.contentItems.length; i++) {
      if (absoluteSizes.additionalPixel - i > 0) {
        absoluteSizes.itemSizes[i]++;
      }

      if (this._isColumn) {
        setElementWidth(
          this.contentItems[i].element,
          absoluteSizes.crossAxisSize,
        );
        setElementHeight(
          this.contentItems[i].element,
          absoluteSizes.itemSizes[i],
        );
      } else {
        setElementWidth(
          this.contentItems[i].element,
          absoluteSizes.itemSizes[i],
        );
        setElementHeight(
          this.contentItems[i].element,
          absoluteSizes.crossAxisSize,
        );
      }
    }
  }

  /**
   * Calculates the absolute sizes of all of the children of this Item.
   * @returns Set with absolute sizes and additional pixels.
   * @internal
   */
  private calculateAbsoluteSizes(): RowOrColumnAbsoluteSizes {
    const totalSplitterSize =
      (this.contentItems.length - 1) * this._splitterSize;
    const { width: elementWidth, height: elementHeight } =
      getElementWidthAndHeight(this.element);

    let totalSize: number;
    let crossAxisSize: number;
    if (this._isColumn) {
      totalSize = Math.max(0, elementHeight - totalSplitterSize);
      crossAxisSize = Math.max(0, elementWidth);
    } else {
      totalSize = Math.max(0, elementWidth - totalSplitterSize);
      crossAxisSize = Math.max(0, elementHeight);
    }

    let totalAssigned = 0;
    const itemSizes = [];

    for (const contentItem of this.contentItems) {
      let itemSize: number;
      if (contentItem.sizeUnit === SizeUnit.Percent) {
        itemSize = Math.floor(totalSize * (contentItem.size / 100));
      } else {
        throw new AssertError('ROCCAS6692');
      }

      totalAssigned += itemSize;
      itemSizes.push(itemSize);
    }

    const additionalPixel = Math.floor(totalSize - totalAssigned);

    return {
      itemSizes,
      additionalPixel,
      totalSize,
      crossAxisSize,
    };
  }

  /**
   * Calculates the relative sizes of all children of this Item. The logic
   * is as follows:
   *
   * - Add up the total size of all items that have a configured size
   *
   * - If the total == 100 (check for floating point errors)
   *        Excellent, job done
   *
   * - If the total is \> 100,
   *        set the size of items without set dimensions to 1/3 and add this to the total
   *        set the size off all items so that the total is hundred relative to their original size
   *
   * - If the total is \< 100
   *        If there are items without set dimensions, distribute the remainder to 100 evenly between them
   *        If there are no items without set dimensions, increase all items sizes relative to
   *        their original size so that they add up to 100
   *
   * @internal
   */
  private calculateRelativeSizes() {
    let total = 0;
    const itemsWithFractionalSize: ContentItem[] = [];
    let totalFractionalSize = 0;

    for (const contentItem of this.contentItems) {
      const sizeUnit = contentItem.sizeUnit;
      switch (sizeUnit) {
        case SizeUnit.Percent: {
          total += contentItem.size;
          break;
        }
        case SizeUnit.Fractional: {
          itemsWithFractionalSize.push(contentItem);
          totalFractionalSize += contentItem.size;
          break;
        }
        default:
          throw new AssertError('ROCCRS49110', JSON.stringify(contentItem));
      }
    }

    /**
     * Everything adds up to hundred, all good :-)
     */
    if (Math.round(total) === 100 && itemsWithFractionalSize.length === 0) {
      this.respectMinItemSize();
      return;
    }

    if (Math.round(total) < 100 && itemsWithFractionalSize.length > 0) {
      this.allocateFractionalSizes(
        itemsWithFractionalSize,
        totalFractionalSize,
        100 - total,
      );
      this.respectMinItemSize();
      return;
    }

    // Reserve another 50% for fractional items before normalizing an oversubscribed row.
    if (Math.round(total) >= 100 && itemsWithFractionalSize.length > 0) {
      this.allocateFractionalSizes(
        itemsWithFractionalSize,
        totalFractionalSize,
        50,
      );
      total += 50;
    }

    if (total === 0) {
      const equalShare = 100 / this.contentItems.length;
      for (const contentItem of this.contentItems) {
        contentItem.size = equalShare;
        contentItem.sizeUnit = SizeUnit.Percent;
      }
    } else {
      for (const contentItem of this.contentItems) {
        contentItem.size = (contentItem.size / total) * 100;
      }
    }

    this.respectMinItemSize();
  }

  private allocateFractionalSizes(
    items: ContentItem[],
    totalFractionalSize: number,
    allocatedSize: number,
  ): void {
    for (const contentItem of items) {
      const fractionalShare =
        totalFractionalSize > 0
          ? contentItem.size / totalFractionalSize
          : 1 / items.length;
      contentItem.size = allocatedSize * fractionalShare;
      contentItem.sizeUnit = SizeUnit.Percent;
    }
  }

  /**
   * Adjusts the column widths to respect the dimensions minItemWidth if set.
   * @internal
   */
  private respectMinItemSize() {
    interface Entry {
      size: number;
      minSize: number;
    }

    if (this.contentItems.length <= 1) {
      return;
    }

    let totalOverMin = 0;
    let totalUnderMin = 0;
    const entriesOverMin: Entry[] = [];
    const allEntries: Entry[] = [];
    const absoluteSizes = this.calculateAbsoluteSizes();

    for (let i = 0; i < absoluteSizes.itemSizes.length; i++) {
      const itemSize = absoluteSizes.itemSizes[i];
      const childMinSize = this.calculateContentItemMinSize(
        this.contentItems[i],
      );
      let entry: Entry;
      if (itemSize < childMinSize) {
        totalUnderMin += childMinSize - itemSize;
        entry = { size: childMinSize, minSize: childMinSize };
      } else {
        totalOverMin += itemSize - childMinSize;
        entry = { size: itemSize, minSize: childMinSize };
        entriesOverMin.push(entry);
      }
      allEntries.push(entry);
    }

    if (totalUnderMin === 0 || totalUnderMin > totalOverMin) {
      return;
    }

    this.reduceEntriesOverMin(
      entriesOverMin,
      allEntries,
      totalUnderMin,
      totalOverMin,
    );
    for (let i = 0; i < this.contentItems.length; i++) {
      this.contentItems[i].size =
        (allEntries[i].size / absoluteSizes.totalSize) * 100;
    }
  }

  private reduceEntriesOverMin(
    entriesOverMin: { size: number; minSize: number }[],
    allEntries: { size: number; minSize: number }[],
    totalUnderMin: number,
    totalOverMin: number,
  ): void {
    const reducePercent = totalUnderMin / totalOverMin;
    let remainingSize = totalUnderMin;
    for (const entry of entriesOverMin) {
      const reducedSize = Math.round(
        (entry.size - entry.minSize) * reducePercent,
      );
      remainingSize -= reducedSize;
      entry.size -= reducedSize;
    }

    // Apply rounding remainder to the last over-min item, as before.
    if (remainingSize !== 0) {
      if (entriesOverMin.length > 0) {
        entriesOverMin[entriesOverMin.length - 1].size -= remainingSize;
      } else if (allEntries.length > 0) {
        allEntries[allEntries.length - 1].size -= remainingSize;
      }
    }
  }

  /**
   * Instantiates a new Splitter, binds events to it and adds
   * it to the array of splitters at the position specified as the index argument
   *
   * What it doesn't do though is append the splitter to the DOM
   *
   * @param index - The position of the splitter
   *
   * @returns
   * @internal
   */
  private createSplitter(index: number): Splitter {
    const splitter = new Splitter(
      this._isColumn,
      this._splitterSize,
      this._splitterGrabSize,
    );
    splitter.on('drag', (offsetX, offsetY) =>
      this.onSplitterDrag(splitter, offsetX, offsetY),
    );
    splitter.on('dragStop', (event) =>
      this.onSplitterDragStop(splitter, event),
    );
    splitter.on('dragStart', () => this.onSplitterDragStart(splitter));
    this._splitter.splice(index, 0, splitter);
    return splitter;
  }

  /**
   * Locates the instance of Splitter in the array of
   * registered splitters and returns a map containing the contentItem
   * before and after the splitters, both of which are affected if the
   * splitter is moved
   *
   * @returns A map of contentItems that the splitter affects
   * @internal
   */
  private getSplitItems(splitter: Splitter) {
    const index = this._splitter.indexOf(splitter);

    return {
      before: this.contentItems[index],
      after: this.contentItems[index + 1],
    };
  }

  private calculateContentItemMinSize(contentItem: ContentItem) {
    const minSize = contentItem.minSize;
    if (minSize !== undefined) {
      if (contentItem.minSizeUnit === SizeUnit.Pixel) {
        return minSize;
      } else {
        throw new AssertError('ROCGMD98831', JSON.stringify(contentItem));
      }
    } else {
      const dimensions = this.layoutManager.layoutConfig.dimensions;
      return this._isColumn
        ? dimensions.defaultMinItemHeight
        : dimensions.defaultMinItemWidth;
    }
  }

  /**
   * Invoked when a splitter's dragListener fires dragStart. Calculates the splitters
   * movement area once (so that it doesn't need calculating on every mousemove event)
   * @internal
   */
  private onSplitterDragStart(splitter: Splitter) {
    const items = this.getSplitItems(splitter);

    const beforeWidth = pixelsToNumber(
      items.before.element.style[this._dimension],
    );
    const afterSize = pixelsToNumber(
      items.after.element.style[this._dimension],
    );
    const beforeMinSize = this.calculateContentItemMinSize(items.before);
    const afterMinSize = this.calculateContentItemMinSize(items.after);

    this._splitterPosition = 0;
    this._splitterMinPosition = -1 * (beforeWidth - beforeMinSize);
    this._splitterMaxPosition = afterSize - afterMinSize;
  }

  /**
   * Invoked when a splitter's DragListener fires drag. Updates the splitter's DOM position,
   * but not the sizes of the elements the splitter controls in order to minimize resize events
   *
   * @param splitter -
   * @param offsetX - Relative pixel values to the splitter's original position. Can be negative
   * @param offsetY - Relative pixel values to the splitter's original position. Can be negative
   * @internal
   */
  private onSplitterDrag(splitter: Splitter, offsetX: number, offsetY: number) {
    let offset = this._isColumn ? offsetY : offsetX;

    if (
      this._splitterMinPosition === null ||
      this._splitterMaxPosition === null
    ) {
      throw new UnexpectedNullError('ROCOSD59226');
    }
    offset = Math.max(offset, this._splitterMinPosition);
    offset = Math.min(offset, this._splitterMaxPosition);

    this._splitterPosition = offset;
    const offsetPixels = numberToPixels(offset);
    if (this._isColumn) {
      splitter.element.style.top = offsetPixels;
    } else {
      splitter.element.style.left = offsetPixels;
    }
  }

  /**
   * Invoked when a splitter's DragListener fires dragStop. Resets the splitters DOM position,
   * and applies the new sizes to the elements before and after the splitter and their children
   * on the next animation frame
   * @internal
   */
  private onSplitterDragStop(splitter: Splitter, event?: PointerEvent): void {
    if (this._splitterPosition === null) {
      throw new UnexpectedNullError('ROCOSDS66932');
    } else {
      splitter.element.style.top = numberToPixels(0);
      splitter.element.style.left = numberToPixels(0);
      if (event?.type === 'pointercancel') {
        this._splitterPosition = null;
        return;
      }
      const items = this.getSplitItems(splitter);
      const sizeBefore = pixelsToNumber(
        items.before.element.style[this._dimension],
      );
      const sizeAfter = pixelsToNumber(
        items.after.element.style[this._dimension],
      );
      const totalSize = sizeBefore + sizeAfter;
      const splitterPositionInRange =
        totalSize > 0 ? (this._splitterPosition + sizeBefore) / totalSize : 0.5;
      const totalRelativeSize = items.before.size + items.after.size;

      items.before.size = splitterPositionInRange * totalRelativeSize;
      items.after.size = (1 - splitterPositionInRange) * totalRelativeSize;

      this._splitterPosition = null;
      if (this._resizeFrame !== undefined) {
        globalThis.cancelAnimationFrame(this._resizeFrame);
      }
      this._resizeFrame = globalThis.requestAnimationFrame(() => {
        this._resizeFrame = undefined;
        if (!this._isDestroyed) {
          this.updateSize(false);
        }
      });
    }
  }
}
